import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, TruckIcon, ShieldCheckIcon, AlertTriangleIcon, TrendingUpIcon, CheckCircleIcon, ClockIcon, XCircleIcon, AlertCircleIcon, FileTextIcon } from 'lucide-react';
import { mockSuppliers } from '../data/mockData';
import { StatusBadge } from '../components/StatusBadge';
export function SupplierKPIDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const supplier = mockSuppliers.find(s => s.id === id);
  if (!supplier) {
    return <div className="flex flex-col items-center justify-center h-full" data-id="element-2751">
        <h2 className="text-xl font-bold text-gray-900" data-id="element-2752">Supplier not found</h2>
        <button onClick={() => navigate('/supplier-kpi')} className="mt-4 text-indigo-600 hover:text-indigo-800" data-id="element-2753">
          Back to KPI Report
        </button>
      </div>;
  }
  const deliveryScore = Math.max(0, 100 - supplier.deliveryDelayPercent * 5);
  const qualityScore = Math.max(0, 100 - supplier.rejectionRatePercent * 10);
  return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-2754">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-2755">
        <div className="flex items-center space-x-4" data-id="element-2756">
          <button onClick={() => navigate('/supplier-kpi')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-2757">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-2758" />
          </button>
          <div data-id="element-2759">
            <div className="flex items-center space-x-3" data-id="element-2760">
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-2761">
                {supplier.name}
              </h1>
              <StatusBadge status={supplier.performanceStatus} data-id="element-2762" />
            </div>
            <p className="text-sm text-gray-500 mt-1" data-id="element-2763">
              {supplier.code} • {supplier.category} • {supplier.countryOfOrigin}
            </p>
          </div>
        </div>
        <div className="flex space-x-3" data-id="element-2764">
          <button className="px-4 py-2 border border-gray-300 bg-white text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors shadow-sm flex items-center" data-id="element-2765">
            <FileTextIcon className="w-4 h-4 mr-2" data-id="element-2766" />
            Export Report
          </button>
        </div>
      </div>

      {/* KPI Overview */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-2767">
        <div className="p-6 border-b border-gray-200 bg-gray-50" data-id="element-2768">
          <h2 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-2769">
            <TrendingUpIcon className="w-5 h-5 mr-2 text-indigo-600" data-id="element-2770" />
            KPI Score Breakdown
          </h2>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-8 divide-y md:divide-y-0 md:divide-x divide-gray-200" data-id="element-2771">
          <div className="flex flex-col items-center justify-center text-center" data-id="element-2772">
            <div className="text-sm font-medium text-gray-500 mb-2" data-id="element-2773">
              Delivery Score (60%)
            </div>
            <div className="relative inline-flex items-center justify-center" data-id="element-2774">
              <svg className="w-32 h-32 transform -rotate-90" data-id="element-2775">
                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-gray-100" data-id="element-2776" />
                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="transparent" strokeDasharray={351.858} strokeDashoffset={351.858 - 351.858 * deliveryScore / 100} className="text-blue-500" strokeLinecap="round" data-id="element-2777" />
              </svg>
              <span className="absolute text-3xl font-bold text-gray-900" data-id="element-2778">
                {deliveryScore}
              </span>
            </div>
            <div className="mt-4 text-sm text-gray-600" data-id="element-2779">
              Based on {supplier.deliveryDelayPercent}% delay rate
            </div>
          </div>

          <div className="flex flex-col items-center justify-center text-center pt-8 md:pt-0" data-id="element-2780">
            <div className="text-sm font-medium text-gray-500 mb-2" data-id="element-2781">
              Quality Score (40%)
            </div>
            <div className="relative inline-flex items-center justify-center" data-id="element-2782">
              <svg className="w-32 h-32 transform -rotate-90" data-id="element-2783">
                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="transparent" className="text-gray-100" data-id="element-2784" />
                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="12" fill="transparent" strokeDasharray={351.858} strokeDashoffset={351.858 - 351.858 * qualityScore / 100} className="text-emerald-500" strokeLinecap="round" data-id="element-2785" />
              </svg>
              <span className="absolute text-3xl font-bold text-gray-900" data-id="element-2786">
                {qualityScore}
              </span>
            </div>
            <div className="mt-4 text-sm text-gray-600" data-id="element-2787">
              Based on {supplier.rejectionRatePercent}% rejection rate
            </div>
          </div>

          <div className="flex flex-col items-center justify-center text-center pt-8 md:pt-0" data-id="element-2788">
            <div className="text-sm font-medium text-gray-500 mb-2" data-id="element-2789">
              Final KPI Score
            </div>
            <div className="relative inline-flex items-center justify-center" data-id="element-2790">
              <svg className="w-40 h-40 transform -rotate-90" data-id="element-2791">
                <circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="16" fill="transparent" className="text-gray-100" data-id="element-2792" />
                <circle cx="80" cy="80" r="70" stroke="currentColor" strokeWidth="16" fill="transparent" strokeDasharray={439.82} strokeDashoffset={439.82 - 439.82 * supplier.kpiScore / 100} className={supplier.kpiScore >= 90 ? 'text-emerald-500' : supplier.kpiScore >= 75 ? 'text-blue-500' : supplier.kpiScore >= 60 ? 'text-amber-500' : 'text-rose-500'} strokeLinecap="round" data-id="element-2793" />
              </svg>
              <div className="absolute flex flex-col items-center" data-id="element-2794">
                <span className="text-4xl font-bold text-gray-900" data-id="element-2795">
                  {supplier.kpiScore}
                </span>
                <span className="text-xs font-medium text-gray-500 mt-1 uppercase tracking-wider" data-id="element-2796">
                  {supplier.performanceStatus}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2797">
        {/* Delivery Breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-2798">
          <div className="p-5 border-b border-gray-200 bg-gray-50 flex items-center justify-between" data-id="element-2799">
            <h2 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-2800">
              <TruckIcon className="w-5 h-5 mr-2 text-blue-600" data-id="element-2801" />
              Delivery Performance
            </h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-2802">
              {supplier.totalDeliveries} Total Deliveries
            </span>
          </div>
          <div className="p-6" data-id="element-2803">
            <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-2804">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100" data-id="element-2805">
                <div className="flex items-center text-emerald-800 mb-1" data-id="element-2806">
                  <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-2807" />
                  <span className="text-sm font-medium" data-id="element-2808">On Time</span>
                </div>
                <div className="text-2xl font-bold text-emerald-900" data-id="element-2809">
                  {supplier.onTimeDeliveries}
                </div>
              </div>
              <div className="bg-rose-50 rounded-lg p-4 border border-rose-100" data-id="element-2810">
                <div className="flex items-center text-rose-800 mb-1" data-id="element-2811">
                  <AlertCircleIcon className="w-4 h-4 mr-1.5" data-id="element-2812" />
                  <span className="text-sm font-medium" data-id="element-2813">Delayed</span>
                </div>
                <div className="text-2xl font-bold text-rose-900" data-id="element-2814">
                  {supplier.delayedDeliveries}
                </div>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-100" data-id="element-2815">
                <div className="flex items-center text-amber-800 mb-1" data-id="element-2816">
                  <ClockIcon className="w-4 h-4 mr-1.5" data-id="element-2817" />
                  <span className="text-sm font-medium" data-id="element-2818">Pending</span>
                </div>
                <div className="text-2xl font-bold text-amber-900" data-id="element-2819">
                  {supplier.pendingDeliveries}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex flex-col justify-center" data-id="element-2820">
                <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-2821">
                  Delay Rate
                </div>
                <div className="text-2xl font-bold text-gray-900" data-id="element-2822">
                  {supplier.deliveryDelayPercent}%
                </div>
              </div>
            </div>

            <div className="space-y-2" data-id="element-2823">
              <div className="flex justify-between text-sm" data-id="element-2824">
                <span className="text-gray-500" data-id="element-2825">
                  On Time (
                  {Math.round(supplier.onTimeDeliveries / supplier.totalDeliveries * 100)}
                  %)
                </span>
                <span className="font-medium text-gray-900" data-id="element-2826">
                  {supplier.onTimeDeliveries}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2827">
                <div className="bg-emerald-500 h-2 rounded-full" style={{
                width: `${supplier.onTimeDeliveries / supplier.totalDeliveries * 100}%`
              }} data-id="element-2828"></div>
              </div>

              <div className="flex justify-between text-sm pt-2" data-id="element-2829">
                <span className="text-gray-500" data-id="element-2830">
                  Delayed (
                  {Math.round(supplier.delayedDeliveries / supplier.totalDeliveries * 100)}
                  %)
                </span>
                <span className="font-medium text-gray-900" data-id="element-2831">
                  {supplier.delayedDeliveries}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2832">
                <div className="bg-rose-500 h-2 rounded-full" style={{
                width: `${supplier.delayedDeliveries / supplier.totalDeliveries * 100}%`
              }} data-id="element-2833"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Quality Breakdown */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-2834">
          <div className="p-5 border-b border-gray-200 bg-gray-50 flex items-center justify-between" data-id="element-2835">
            <h2 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-2836">
              <ShieldCheckIcon className="w-5 h-5 mr-2 text-emerald-600" data-id="element-2837" />
              Quality Performance
            </h2>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800" data-id="element-2838">
              {supplier.totalItemsInspected} Items Inspected
            </span>
          </div>
          <div className="p-6" data-id="element-2839">
            <div className="grid grid-cols-2 gap-4 mb-6" data-id="element-2840">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100" data-id="element-2841">
                <div className="flex items-center text-emerald-800 mb-1" data-id="element-2842">
                  <CheckCircleIcon className="w-4 h-4 mr-1.5" data-id="element-2843" />
                  <span className="text-sm font-medium" data-id="element-2844">Accepted</span>
                </div>
                <div className="text-2xl font-bold text-emerald-900" data-id="element-2845">
                  {supplier.itemsAccepted}
                </div>
              </div>
              <div className="bg-rose-50 rounded-lg p-4 border border-rose-100" data-id="element-2846">
                <div className="flex items-center text-rose-800 mb-1" data-id="element-2847">
                  <XCircleIcon className="w-4 h-4 mr-1.5" data-id="element-2848" />
                  <span className="text-sm font-medium" data-id="element-2849">Rejected</span>
                </div>
                <div className="text-2xl font-bold text-rose-900" data-id="element-2850">
                  {supplier.itemsRejected}
                </div>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-100" data-id="element-2851">
                <div className="flex items-center text-amber-800 mb-1" data-id="element-2852">
                  <AlertTriangleIcon className="w-4 h-4 mr-1.5" data-id="element-2853" />
                  <span className="text-sm font-medium" data-id="element-2854">Under Review</span>
                </div>
                <div className="text-2xl font-bold text-amber-900" data-id="element-2855">
                  {supplier.itemsUnderReview}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 flex flex-col justify-center" data-id="element-2856">
                <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-2857">
                  Rejection Rate
                </div>
                <div className="text-2xl font-bold text-gray-900" data-id="element-2858">
                  {supplier.rejectionRatePercent}%
                </div>
              </div>
            </div>

            <div className="space-y-2" data-id="element-2859">
              <div className="flex justify-between text-sm" data-id="element-2860">
                <span className="text-gray-500" data-id="element-2861">
                  Accepted (
                  {Math.round(supplier.itemsAccepted / supplier.totalItemsInspected * 100)}
                  %)
                </span>
                <span className="font-medium text-gray-900" data-id="element-2862">
                  {supplier.itemsAccepted}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2863">
                <div className="bg-emerald-500 h-2 rounded-full" style={{
                width: `${supplier.itemsAccepted / supplier.totalItemsInspected * 100}%`
              }} data-id="element-2864"></div>
              </div>

              <div className="flex justify-between text-sm pt-2" data-id="element-2865">
                <span className="text-gray-500" data-id="element-2866">
                  Rejected (
                  {Math.round(supplier.itemsRejected / supplier.totalItemsInspected * 100)}
                  %)
                </span>
                <span className="font-medium text-gray-900" data-id="element-2867">
                  {supplier.itemsRejected}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-2868">
                <div className="bg-rose-500 h-2 rounded-full" style={{
                width: `${supplier.itemsRejected / supplier.totalItemsInspected * 100}%`
              }} data-id="element-2869"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className={`rounded-xl border p-6 ${supplier.kpiScore >= 75 ? 'bg-emerald-50 border-emerald-200' : supplier.kpiScore >= 60 ? 'bg-amber-50 border-amber-200' : 'bg-rose-50 border-rose-200'}`} data-id="element-2870">
        <h3 className={`text-lg font-medium mb-2 ${supplier.kpiScore >= 75 ? 'text-emerald-900' : supplier.kpiScore >= 60 ? 'text-amber-900' : 'text-rose-900'}`} data-id="element-2871">
          System Recommendation
        </h3>
        <p className={`text-sm ${supplier.kpiScore >= 75 ? 'text-emerald-800' : supplier.kpiScore >= 60 ? 'text-amber-800' : 'text-rose-800'}`} data-id="element-2872">
          {supplier.kpiScore >= 90 ? 'This supplier is performing exceptionally well. Consider them for strategic partnerships and long-term contracts. They are eligible for preferred supplier status.' : supplier.kpiScore >= 75 ? 'This supplier is performing well and meeting expectations. Continue regular monitoring. They are eligible for standard contract renewals.' : supplier.kpiScore >= 60 ? 'This supplier requires performance improvement. Schedule a review meeting to discuss delivery and quality issues. Consider short-term contracts until performance improves.' : 'This supplier is severely underperforming. Immediate corrective action plan required. Consider suspending new orders until issues are resolved or initiating vendor replacement.'}
        </p>
      </div>
    </div>;
}